import React, { useState, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import config from "../../../config";
import logo from "../../../assets/images/header-logo.png";
import "./AuthModal.css";

const AuthModal = ({ open, onClose, initialMode = "login" }) => {
  const { login, loading } = useContext(AuthContext);
  const [mode, setMode] = useState(initialMode);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const redirectPath = location.state?.from || "/";

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    setError("");
    setSuccess("");

    if (mode === "signup") {
      if (!isValidEmail(normalizedEmail)) {
        setError("Please enter a valid email address");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }

      try {
        await axios.post(`${config.apiBaseUrl}/api/auth/signup`, {
          firstName,
          lastName,
          email: normalizedEmail,
          password,
        });
        const credentials = { email: normalizedEmail, password };
        await login(credentials, redirectPath);
        setSuccess("Sign up successful! Redirecting...");
        setTimeout(() => {
          onClose();
          navigate(redirectPath);
        }, 1000);
      } catch (error) {
        console.error(
          "Sign-up error:",
          error.response ? error.response.data : error.message
        );
        setError(
          error.response?.data?.message || "Error signing up. Try again."
        );
      }
    } else {
      try {
        const credentials = { email: normalizedEmail, password };
        const path = await login(credentials, redirectPath);
        setSuccess("Login successful! Redirecting...");
        setTimeout(() => {
          onClose();
          navigate(path);
        }, 1000);
      } catch (error) {
        console.error(
          "Error during login:",
          error.response ? error.response.data : error.message
        );
        setError("Invalid credentials!");
      }
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setError("");
    setSuccess("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="auth-modal-title"
      maxWidth="sm"
      fullWidth
      className="auth-modal__dialog"
    >
      <DialogTitle id="auth-modal-title" className="auth-modal__header">
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <a href="/" className="auth-modal__logo-link">
            <img
              src={logo}
              alt="Air Kitchenz Logo"
              className="auth-modal__logo-image"
            />
          </a>
          <Typography
            variant="h5"
            component="h1"
            fontWeight={700}
            color="text.primary"
          >
            {mode === "login" ? "Welcome Back!" : "Join AirKitchenz!"}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          className="auth-modal__close-btn"
          aria-label="Close modal"
          sx={{ position: "absolute", top: 16, right: 16 }}
        >
          <Typography variant="body1" color="text.secondary">
            ✕
          </Typography>
        </IconButton>
      </DialogTitle>
      <DialogContent className="auth-modal__content">
        {error && (
          <Box className="auth-modal__error-message" role="alert">
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
        {success && (
          <Box className="auth-modal__success-message" role="alert">
            <Typography variant="body2" color="success.main">
              {success}
            </Typography>
          </Box>
        )}
        {!success && (
          <Box
            component="form"
            onSubmit={handleSubmit}
            className="auth-modal__form"
          >
            {mode === "signup" && (
              <>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  disabled={loading}
                  aria-label="First Name"
                  autoComplete="given-name"
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  disabled={loading}
                  aria-label="Last Name"
                  autoComplete="family-name"
                />
              </>
            )}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              variant="outlined"
              disabled={loading}
              aria-label="Email Address"
              autoComplete="email"
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              margin="normal"
              variant="outlined"
              disabled={loading}
              aria-label="Password"
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {mode === "signup" && (
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                margin="normal"
                variant="outlined"
                disabled={loading}
                aria-label="Confirm Password"
                autoComplete="new-password"
              />
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              className="auth-modal__submit-btn"
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : mode === "login" ? (
                "Log In"
              ) : (
                "Sign Up"
              )}
            </Button>
            <Box mt={2} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                {mode === "login"
                  ? "Don't have an account? "
                  : "Already have an account? "}
                <Typography
                  component="span"
                  variant="body2"
                  color="primary.main"
                  onClick={toggleMode}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 600,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {mode === "login" ? "Sign Up" : "Log In"}
                </Typography>
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
